// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useMemo } from "react";
import { LayoutGrid, Plus, Table2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchField from "@/components/common/SearchField";
import FilterChips from "@/components/common/FilterChips";
import TaskTable from "@/components/deals/tasks/TaskTable";
import { TaskGroups } from "@/components/deals/command/CommandCenter";
import { buildTaskGroups } from "@/utils/deals-command.utils";
import { cn } from "@/utils/common.utils";

/**
 * Tasks & Timeline.
 *
 * Two views behind one switch. Grouped is the default and is what an agent
 * sees on open; All tasks is the full spreadsheet, unchanged, one click away.
 * The brief: *"Keep the full task table available under All Tasks, but redesign
 * the default … If an agent needs the full spreadsheet-like tracker, they can
 * click All Tasks."*
 *
 * The redesign moved this tab's three top blocks — Needs attention, This week
 * and the stage rail — up to the deal page itself, so what remains here is the
 * work: four drawers, or the table. The switch is the only control for that
 * choice; an earlier draft also put an "All tasks" button above the tab bar,
 * which was two controls doing one job.
 */
export default function TimelineTab({
  deal, view, onViewChange,
  taskSearch, onSearchChange, taskPhase, onPhaseChange, phaseFilters,
  visibleTasks, onToggle, onAddTask, taskCount = 0,
  onAddContingency = null,
  onNudge = null,
  onEditTask = null,
  onReorder = null,
  onReleaseTask = null,
  onConfirmTask = null,
  clientActionTaskId = null,
}) {
  const isCommand = view === "command";
  const groups = useMemo(() => buildTaskGroups({ tasks: visibleTasks }), [visibleTasks]);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <ViewSwitch view={view} onViewChange={onViewChange} taskCount={taskCount} />

        {/* Search and the phase chips belong to the spreadsheet. In the grouped
            view the grouping *is* the filter, and carrying a phase chip over
            would silently hide rows from drawers whose counts claim otherwise. */}
        {!isCommand && (
          <>
            <SearchField className="w-full sm:min-w-[240px] sm:flex-1" value={taskSearch} onChange={(event) => onSearchChange(event.target.value)} onClear={() => onSearchChange("")} placeholder="Search tasks..." shortcut="⌘K" inputClassName="h-[41px] rounded-xl border-dash-border bg-white" aria-label="Search tasks" />
            <div className="w-full sm:w-auto">
              <FilterChips
                items={phaseFilters}
                value={taskPhase}
                onValueChange={onPhaseChange}
                itemClassName="h-7 rounded-[8px] px-3 text-xs aria-pressed:border-transparent aria-pressed:bg-primary aria-pressed:bg-none aria-pressed:text-primary-foreground"
                scrollableOnMobile
                testIdPrefix="task-phase"
              />
            </div>
          </>
        )}

        <div className="flex w-full flex-col gap-3 sm:ml-auto sm:w-auto sm:flex-row">
          {onAddContingency && (
            <Button type="button" variant="dashboardOutline" onClick={onAddContingency} className="h-[38px] w-full !rounded-[11px] px-4 sm:w-auto" data-testid="add-contingency-btn">
              <Plus data-icon="inline-start" /> Add contingency
            </Button>
          )}
          {onAddTask && (
            <Button type="button" onClick={onAddTask} className="h-[38px] w-full !rounded-[11px] bg-primary px-4 text-primary-foreground shadow-sm hover:bg-primary-hover active:bg-primary-pressed sm:w-auto" data-testid="add-task-btn">
              <Plus data-icon="inline-start" /> Add task
            </Button>
          )}
        </div>
      </div>

      {isCommand ? (
        <TaskGroups
          groups={groups}
          onToggleTask={onToggle}
          onEditTask={onEditTask}
          onReleaseTask={onReleaseTask}
          onConfirmTask={onConfirmTask}
          clientActionTaskId={clientActionTaskId}
          onReorder={onReorder}
        />
      ) : (
        <TaskTable
          tasks={visibleTasks}
          deal={deal}
          onToggle={onToggle}
          onNudge={onNudge}
          onEditTask={onEditTask}
          onReorder={onReorder}
          onReleaseTask={onReleaseTask}
          onConfirmTask={onConfirmTask}
          clientActionTaskId={clientActionTaskId}
        />
      )}
    </div>
  );
}

function ViewSwitch({ view, onViewChange, taskCount }) {
  // The count rides on the control that already exists rather than on a second
  // button above the tab bar saying the same thing.
  const options = [
    { value: "command", label: "Grouped", Icon: LayoutGrid },
    { value: "all", label: taskCount ? `All tasks (${taskCount})` : "All tasks", Icon: Table2 },
  ];
  return (
    <div className="inline-flex shrink-0 rounded-[10px] border border-dash-border bg-white p-0.5" role="tablist" aria-label="Task view">
      {options.map(({ value, label, Icon }) => {
        const active = view === value;
        return (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onViewChange(value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-xs font-medium transition-colors",
              active ? "bg-primary text-primary-foreground" : "text-dash-muted hover:text-dash-ink",
            )}
            data-testid={`task-view-${value}`}
          >
            <Icon className="size-3.5" /> {label}
          </button>
        );
      })}
    </div>
  );
}
